/**
 * Phase B — slash-command skills registry.
 *
 * Each entry is a discoverable "/skill" the user can type into the input
 * box or pick from the slash autocomplete. The skills route to deterministic
 * actions: the existing wizard, the existing /scaffold endpoint, the existing
 * /commentary endpoint, or new deterministic builders (added in B2 /forecast
 * and B6 /clean-data).
 *
 * Skills with kind = 'placeholder' fall back to a prompt the user can send
 * to the agent — interim state for skills whose deterministic builder hasn't
 * shipped yet. They are flagged in the UI as "(prompt)" so the user knows.
 */

export type SkillKind =
  | 'wizard'           // open an existing modal
  | 'scaffold'         // call buildDashboardFromWizard with a fixed archetype
  | 'commentary'       // call /api/workbook/commentary directly
  | 'forecast'         // B2 — deterministic forecast builder (placeholder until B2 lands)
  | 'clean_data'       // B6 — deterministic data-quality analyzer (placeholder until B6 lands)
  | 'discuss'          // 2026-06-16 — turn on Discussion / Analysis mode (no build)
  | 'prompt';          // fill the input with a prompt template

export interface Skill {
  slash: string;       // user-typed trigger including leading slash, e.g. "/forecast"
  emoji: string;
  label: string;       // short title shown in the popover
  description: string; // one-line subtitle
  kind: SkillKind;
  /** For kind = 'scaffold': the archetype to build via buildScaffoldTool. */
  archetype?: string;
  /** For kind = 'scaffold': default theme. corporate_navy if omitted. */
  theme?: string;
  /** For kind = 'prompt': the text dropped into the input box. */
  prompt?: string;
  /** Optional flag — true while the deterministic implementation is in flight. */
  comingSoon?: boolean;
}

export const SKILLS: Skill[] = [
  // 2026-06-16 — Discussion / Analysis mode. Unlike the other skills this
  // doesn't kick off a build — it flips the Build<->Discuss toggle to Discuss
  // and focuses the input so the user can ask a reasoning / scenario / "what
  // do you think?" question. The backend then appends the DISCUSSION_ANALYSIS
  // protocol and answers in chat instead of writing a worksheet.
  {
    slash: '/discuss',
    emoji: '💭',
    label: 'Discuss / analyse',
    description: 'Switch to discussion mode — reason through a scenario, assumptions, or "what do you think?" in chat (no worksheet built).',
    kind: 'discuss',
  },
  {
    slash: '/dashboard',
    emoji: '🪄',
    label: 'Build dashboard',
    description: 'Open the 6-question wizard for a deterministic board-pack dashboard.',
    kind: 'wizard',
  },
  {
    slash: '/management-report',
    emoji: '📊',
    label: 'Management report',
    description: 'Monthly P&L dashboard with KPI tiles, breakdowns, charts, commentary.',
    kind: 'scaffold',
    archetype: 'board_pl_monthly',
    theme: 'corporate_navy',
  },
  {
    slash: '/forecast',
    emoji: '🏗',
    label: 'Forecast',
    description: 'Project a time-series with adjustable growth assumptions in yellow input cells.',
    kind: 'forecast',
  },
  {
    slash: '/commentary',
    emoji: '💬',
    label: 'Commentary',
    description: 'CFO-ready 3-paragraph commentary tied to the actual numbers.',
    kind: 'commentary',
  },
  {
    slash: '/clean-data',
    emoji: '🧹',
    label: 'Clean data',
    description: 'Scan the active sheet for blanks, duplicates, format issues, outliers.',
    kind: 'clean_data',
  },
  // 2026-05-28 — Phase 2 of the skills + standards roadmap. Backed by the
  // COMPILE_FINANCIALS section in systemPrompt.ts which gives the agent a
  // verbatim 7-step protocol: ask the user for reporting tier → snapshot
  // workbook → call generate_disclosure_checklist → build Statement of
  // P&L + BS + Changes in Equity + Cash Flows + Notes via apply_workbook_plan
  // with formula references throughout (RULE R1) → format per RULE R9 →
  // audit-trail metadata per RULE R8 → short summary. Pure agent
  // orchestration on top of existing infra; deterministic scaffold version
  // is a future v2 enhancement.
  {
    slash: '/compile-financials',
    emoji: '📑',
    label: 'Compile financials',
    description: 'Build a full Tier 2 GPFR set — P&L, BS, Changes in Equity, Cash Flows, Notes — with formula references to the source.',
    kind: 'prompt',
    prompt: 'Compile a full Tier 2 GPFR set of financial statements from this workbook. Follow the /compile-financials protocol: ask me to confirm the reporting tier, snapshot the P&L and BS sheets, run the disclosure checklist, then build the Statement of P&L, Statement of Financial Position, Statement of Changes in Equity, Statement of Cash Flows, and Notes — all on new sheets with formula references back to the source data (no hardcoded numbers). Apply standard formatting and audit-trail metadata. Tell me at the end which notes are auto-stubbed and which need my input.',
  },
  // 2026-05-28 — Phase 3 of the skills + standards roadmap. Backed by the
  // TAX_RETURN_REVIEW section in systemPrompt.ts which gives the agent a
  // verbatim 6-step protocol: read full PDF → reconciliation check
  // (Item 6 → 7 → 8 → 9) → ATO compliance checklist → AASB cross-check
  // (if workbook present) → structured bullet output → optional Excel
  // sheet write. The PDF must be attached via the 📎 button first.
  {
    slash: '/tax-return-review',
    emoji: '🔎',
    label: 'Tax return review',
    description: 'Read an attached CTR PDF, reconcile to taxable income, cross-check ATO + AASB compliance, flag anomalies.',
    kind: 'prompt',
    prompt: 'Review the attached Company Tax Return PDF using the /tax-return-review protocol. Reconcile Item 6 → Item 7 → Item 8 → Item 9. Run the ATO compliance checklist for the common items (depreciation, provisions, R&D, franking, related parties, Div 7A, loss carry-forward, etc.). If a workbook is also ingested, cross-check against AASB 112, 119, 16, 124 using validate_standard. Output a bullet-pointed review memo with specific page + Item citations for every finding. Include management actions required and the standard AI-interpretive disclaimer.',
  },
];

/** Case-insensitive prefix match against either the slash trigger or the label. */
export function filterSkills(query: string): Skill[] {
  const q = query.trim().toLowerCase();
  if (!q) return SKILLS;
  return SKILLS.filter(sk =>
    sk.slash.toLowerCase().startsWith(q) ||
    sk.label.toLowerCase().includes(q.replace(/^\//, ''))
  );
}

export function getSkillByExactSlash(slash: string): Skill | null {
  return SKILLS.find(sk => sk.slash.toLowerCase() === slash.trim().toLowerCase()) ?? null;
}
